import { Buffer } from 'buffer';
import currency from 'currency.js';
import { addHours, compareAsc, format, parse, parseISO } from 'date-fns';
import isEmpty from 'lodash-es/isEmpty';
import isNaN from 'lodash-es/isNaN';
import isNumber from 'lodash-es/isNumber';

import { PREVIEW_STUDIO_URL } from '../constants/routeUrls';

export const defaultCurrencyOptions = { symbol: '', precision: 7 };

export const isBlank = (value) => (isEmpty(value) && !isNumber(value)) || isNaN(value);

// True for a host/URL we run locally (localhost / loopback). We control such a
// host's CORS, so the browser can call it directly; a remote host cannot allow
// the localhost origin, so dev routes those through the ACH proxy instead. Used
// by useConfig getDefault* to decide per-env whether ACH needs the proxy.
//
// Matches on the parsed hostname (not a substring) so unrelated hosts like
// `notlocalhost.example.com` or `localhost.attacker.com` are NOT treated as
// local. Accepts a full URL (`http://localhost:8080`) or a bare hostname
// (`localhost`, `[::1]`).
const LOCALHOST_HOSTNAMES = ['localhost', '127.0.0.1', '::1', '[::1]'];
export const isLocalhostUrl = (host: string): boolean => {
  if (!host) return false;
  let hostname = host;
  try {
    hostname = new URL(host.includes('://') ? host : `http://${host}`).hostname;
  } catch {
    // Not a parseable URL — fall back to matching the raw string.
  }
  return LOCALHOST_HOSTNAMES.includes(hostname);
};

// convert to use datefns
export const mmddyy = (dateStr: string): string => {
  const today = new Date(dateStr);
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yy = today.getFullYear().toString().substr(-2);

  return `${mm}/${dd}/${yy}`;
};

// TODO: Looking at the server code, the server just converts the dates to EST.
// https://github.com/ThinkNear/ventura/blob/master/app/models/admin/targeted_creative.rb#L147:L147
// The server should ignore the time and timezone before converting it to ESTparseMMddyyyy.
export const formatServerDate = (date) => {
  let returnValue;
  if (date) {
    // If the date is in string format, we are reading the date and displaying it in the date picker
    // In that case, we just want to strip off the time and just display the date in the user's timezone at 12am.
    // parseISO will do that.
    if (typeof date === 'string') {
      returnValue = parseISO(date.split('T')[0]);
    } else {
      // Otherwise, just keep using the local timezone.
      returnValue = date;
    }
  }

  return returnValue;
};

// Human-readable local-time formatting for ISO timestamps (e.g. the custom-segment
// task list, where the time-of-day matters). Blank when there's nothing to show,
// and passes the original string through if it's not a valid ISO date so we don't
// silently swallow garbage from the API.
export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '';
  const parsed = parseISO(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return format(parsed, 'MMM d, yyyy h:mm a');
};

// Reporting timestamps rendered in America/New_York rather than the browser's zone, matching the
// "today"-in-ET anchoring in ContractsTable: the data behind these is reported in ET, and ad-ops
// users span timezones, so a local clock would disagree with both the source and the Ventura pages
// users compare against. The zone abbreviation is always shown (DST-aware: EDT/EST) so the reading
// can't be mistaken for local time. Same blank/pass-through contract as formatDateTime.
export const formatDateTimeEt = (value: string | null | undefined): string => {
  if (!value) return '';
  const parsed = parseISO(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).formatToParts(parsed);
  const pick = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  // Assembled from parts rather than the default joined string so the output matches
  // formatDateTime's "MMM d, yyyy h:mm a" shape (Intl would put a comma before the time).
  return `${pick('month')} ${pick('day')}, ${pick('year')} ${pick('hour')}:${pick('minute')} ${pick('dayPeriod')} ${pick('timeZoneName')}`;
};

// Date-only display formatter. Uses formatServerDate to strip the time/zone so
// a UTC ISO like "2019-03-24T02:39:17Z" doesn't shift a day in negative offsets,
// then renders just the calendar date. For fields where the time-of-day is noise
// (e.g. line-item start/end dates in the associated ad-groups table).
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const parsed = formatServerDate(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return value;
  return format(parsed, 'MMM d, yyyy');
};

export const upperCaseAllFirstLetters = (str: string): string =>
  str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

export const replaceUnderscores = (str: string): string => str.replace(/_/g, ' ');

export const replaceSpacesWithPlus = (str: string): string => str.replace(/\s/g, '+');

export const makeReadable = (str: string): string => upperCaseAllFirstLetters(replaceUnderscores(str));

export const parseMMddyyyy = (date: string): Date => {
  const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
  return parsedDate;
};

export const formatYearFirst = (date: string): string => {
  const parsedDate = parseMMddyyyy(date);
  const formattedDate = format(parsedDate, 'yyyy-MM-dd');
  return formattedDate;
};

export const isChronological = (start: string, end: string): number => {
  const startDate = parseMMddyyyy(start);
  const endDate = parseMMddyyyy(end);
  return compareAsc(endDate, startDate);
};

// currency helpers
export const parseDollarsFromApiPennyValue = (value) => {
  if (value) {
    return `${(value / 100).toFixed(2)}`;
  }
  return 0;
};

export const convertToPenniesString = (value: string | number): string => currency(value).intValue.toString();

export const removeCommasAndOmitCents = (value: string | number): string => currency(value).dollars().toString();

export const removeCommas = (value: string | number): string | number =>
  (!isBlank(value) && value.toString().replace(/,/g, '')) || value;

export const addCommas = (value: string | number): string | number => {
  if (isBlank(value)) {
    return value;
  }

  const stringArray = value.toString().split('.');
  let returnValue = currency(value).dollars().toLocaleString('en-US');
  if (stringArray.length > 1) {
    returnValue += `.${stringArray[1]}`;
  }

  return returnValue;
};

// ADOPS-2556: Compact device-count formatting, e.g. 20000 -> "20k", 20500 -> "20.5k",
// 500 -> "500". Keeps at most one decimal and drops a trailing ".0".
export const formatDeviceCountShort = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '';
  }
  if (Math.abs(value) < 1000) {
    return `${value}`;
  }
  const thousands = Math.round((value / 1000) * 10) / 10;
  return `${thousands}k`;
};

// Round value to 0, 2, or 7 decimal places
export const round = (value: string | number, options: currency.Options = defaultCurrencyOptions): string => {
  // Round off value as cents
  const twoDecimalPlaces = currency(value);
  if (twoDecimalPlaces.cents() === 0) {
    return addCommas(twoDecimalPlaces.dollars()).toString();
  }

  // Right pad it with zeros and compare it with the 5 rounded decimal places
  const paddedTwoDecimalPlaces = currency(twoDecimalPlaces.value, options);
  const sevenDecimalPlaces = currency(value, options);
  // If they are the same, it means we don't need to round up to to max decimal places
  // and we can just keep the original value.
  const twoOrSevenDecimalPlaces =
    paddedTwoDecimalPlaces.value === sevenDecimalPlaces.value ? twoDecimalPlaces : sevenDecimalPlaces;

  return twoOrSevenDecimalPlaces.format(options);
};

export const omitCents = (value: string | number): string => currency(value).dollars().toString();

export const calculateDollarBudget = (bidType: string, contractRate: string, advisoryBudget: string): string => {
  let returnValue;
  const parsedBudget = currency(advisoryBudget, defaultCurrencyOptions);
  const parsedRate = currency(contractRate, defaultCurrencyOptions);

  if (bidType === 'CPM' || bidType === 'CPA' || bidType === 'CPV') {
    returnValue = parsedBudget.divide(1000).multiply(parsedRate);
  } else {
    returnValue = parsedBudget.multiply(parsedRate);
  }

  return round(returnValue.value);
};

export const calculateAdvisoryBudget = (bidType: string, contractRate: string, dollarBudget: string): string => {
  const parsedRate = currency(contractRate, defaultCurrencyOptions);
  let parsedDollarBudget = currency(dollarBudget, defaultCurrencyOptions);

  if (bidType === 'CPM' || bidType === 'CPA' || bidType === 'CPV') {
    parsedDollarBudget = parsedDollarBudget.multiply(1000);
  }

  const returnValue = parsedRate.value ? parsedDollarBudget.divide(parsedRate) : currency(0, defaultCurrencyOptions);

  return round(returnValue.value);
};

export const convertJsDateToHourOfWeek = (jsDate: Date): string => {
  const hour = Number(format(jsDate, 'H'));
  const dayOfWeek = Number(format(jsDate, 'i')) % 7;
  const returnValue = dayOfWeek * 24 + hour;
  return returnValue.toString();
};

export const convertHourOfWeekToJsDate = (hourOfWeekString: number, startDateString: string): Date => {
  const hourOfWeek = Number(hourOfWeekString);
  const startDate = new Date(startDateString);
  const returnValue = addHours(startDate, hourOfWeek);
  return returnValue;
};

export const removeFileExtension = (fileName) => fileName.replace(/\.[^/.]+$/, '');

export const openPreviewPage = async (html: string, macros: [string, string][] = [], service?: string) => {
  let localHtml: string = html;
  // do nothing if no html is entered
  if (!html || html === '') {
    throw new Error("Nothing entered into the 'HTML' field!");
  }

  // loop through macros and replace
  if (macros.length > 0) {
    macros.forEach((macro) => {
      localHtml = localHtml.replace(macro[0], macro[1]);
    });
  }

  // validate html (hard to break DOMParser, but here for safety)
  const doc = new DOMParser().parseFromString(localHtml, 'text/html');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Invalid HTML content! - Open the developer console for more details.');
  }

  if (service === 'testatag') {
    const base64String = Buffer.from(localHtml).toString('base64');
    window.open(`https://test-a-tag.com/#v2,1,${base64String}`, '_blank');
    return;
  }

  const base64Html = Buffer.from(localHtml).toString('base64');
  window.open(`${PREVIEW_STUDIO_URL}?raw_tag=true&tag_html=${base64Html}`, '_blank');
};

export const openNewPreviewPage = async (html, macros = []) => {
  let localHtml = html;

  // Do nothing if no HTML is entered
  if (!html || html === '') {
    throw new Error("Nothing entered into the 'HTML' field!");
  }

  // Loop through macros and replace
  if (macros.length > 0) {
    macros.forEach((macro) => {
      localHtml = localHtml.replace(macro[0], macro[1]);
    });
  }

  // Validate HTML (hard to break DOMParser, but here for safety)
  const doc = new DOMParser().parseFromString(localHtml, 'text/html');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Invalid HTML content! - Open the developer console for more details.');
  }

  // Encode HTML
  const encodedHtml = encodeURIComponent(localHtml);

  const base64Html = Buffer.from(encodedHtml).toString('base64');
  window.open(`${PREVIEW_STUDIO_URL}?raw_tag=true&tag_html=${base64Html}`, '_blank');
};

// MRAID Creative Tag Generator
export const mraidSimulationScript = `
    // MRAID Simulation Script
    var EventTarget = function() {
      this.listeners = {};
    };
    EventTarget.prototype.listeners = null;
    EventTarget.prototype.addEventListener = function(type, callback) {
      if (!(type in this.listeners)) {
        this.listeners[type] = [];
      }
      this.listeners[type].push(callback);
    };
    EventTarget.prototype.removeEventListener = function(type, callback) {
      if (!(type in this.listeners)) {
        return;
      }
      var stack = this.listeners[type];
      for (var i = 0, l = stack.length; i < l; i++) {
        if (stack[i] === callback){
          stack.splice(i, 1);
          return;
        }
      }
    };
    EventTarget.prototype.dispatchEvent = function(event) {
      if (!(event.type in this.listeners)) {
        return true;
      }
      var args = Array.prototype.slice.call(arguments, 1);
      var stack = this.listeners[event.type];
      for (var i = 0, l = stack.length; i < l; i++) {
        stack[i].apply(this, args);
      }
      return !event.defaultPrevented;
    };

    new function(global) {
      var _state = 'loading';
      var _stateChange = function(state) {
        if (_state !== state) {
          _state = state;
          mraid.dispatchEvent(new CustomEvent('stateChange'), state);
        }
      };
      var _expandProperties = false;
      var _resizeProperties = false;
      var _origProperties;
      var _lazyLoad = function() {
        _origProperties = _origProperties || {
          'width': document.body.offsetWidth,
          'height': document.body.offsetHeight,
          'useCustomClose': false,
          'isModal': true,
          'top': document.body.getBoundingClientRect().y,
          'left': document.body.getBoundingClientRect().x
        };
      };
      var mraid = new EventTarget();
      mraid.getState = function() {
        return _state;
      };
      mraid.getVersion = function() {
        return '2.0';
      };
      mraid.open = function(URL) {
        window.open(URL);
      };
      mraid.close = function() {
        switch (_state) {
          case 'expanded':
            window.parent.postMessage({'sig': 'CLOSE'}, '*');
            _stateChange('default');
            break;
          case 'resized':
            document.body.style['margin-top'] = _origProperties.top;
            document.body.style['margin-left'] = _origProperties.left;
            document.body.style['height'] = _origProperties.height;
            document.body.style['width']  = _origProperties.width;
            _stateChange('default');
            break;
        }
      };
      mraid.useCustomClose = function() {
        if (_expandProperties) {
          _expandProperties.useCustomClose = true;
        } else {
          _lazyLoad();
          _origProperties.useCustomClose = true;
        }
      };
      mraid.expand = function(URL) {
        if (URL !== undefined) {
          window.parent.postMessage({'sig': 'EXPAND', 'val': URL}, '*');
        } else {
          _lazyLoad();
          _enableCloseRegion(_origProperties.left + (_expandProperties ? _expandProperties.width : _origProperties.width),
            _origProperties.top,
            _expandProperties && !_expandProperties.useCustomClose || !_origProperties.useCustomClose);
        }
        _stateChange('expanded');
      };
      mraid.getExpandProperties = function() {
        if (_expandProperties) {
          return _expandProperties;
        } else {
          _lazyLoad();
          return {
            'width': _origProperties.width,
            'height': _origProperties.height,
            'useCustomClose': false,
            'isModal': true
          }
        }
      };
      mraid.setExpandProperties = function(obj) {
        _expandProperties = obj;
      };
      mraid.getPlacementType = function() {
        return "inline";
      };
      mraid.isViewable = function() {
        return true;
      };
      mraid.resize = function() {
        if (!_resizeProperties) {
          mraid.dispatchEvent(new CustomEvent('error'));
        } else {
          _lazyLoad();
          document.body.style['margin-top'] = _origProperties.top + _resizeProperties.offsetY;
          document.body.style['margin-left'] = _origProperties.left + _resizeProperties.offsetX;
          document.body.style['height'] = _resizeProperties.height;
          document.body.style['width']  = _resizeProperties.width;
          _enableCloseRegion(_origProperties.left + _resizeProperties.offsetX + _resizeProperties.width,
              _origProperties.top, false);
          _stateChange('resized');
        }
      };
      mraid.setResizeProperties = function (obj) {
        _resizeProperties = obj;
      };
      mraid.getResizeProperties = function() {
        return _resizeProperties;
      };
      mraid.getScreenSize = function() {
        var ls = window.localStorage;
        if (ls) {
          return JSON.parse(ls.viewport);
        } else {
          return {
            width: _origProperties.width,
            height: _origProperties.height
          };
        }
      };
      mraid.getMaxSize = function() {
        return {
          width: _origProperties.width,
          height: _origProperties.height
        };
      };
      mraid.getDefaultPosition = function() {
        _lazyLoad();
        return {
          x: 0,
          y: 0,
          width: _origProperties.width,
          height: _origProperties.height
        };
      };
      mraid.getCurrentPosition = function() {
        switch (_state) {
          case 'expanded':
            if (!_expandProperties) {
              return this.getDefaultPosition();
            } else {
              return {
                x: 0,
                y: 0,
                width: _expandProperties.width,
                height: _expandProperties.height
              };
            }
            break;
          case 'resized':
            break;
          default:
            return this.getDefaultPosition();
        }
      };
      mraid.supports = function(val) {
        switch (val) {
          case 'sms':
            return false;
          case 'tel':
            return false;
          case 'calendar':
            return false;
          case 'storePicture':
            return false;
          case 'inlineVideo':
            return false;
        }
      };
      var _orientationProperties;
      mraid.setOrientationProperties = function(obj) {
        _orientationProperties = obj;
      };
      mraid.getOrientationProperties = function() {
        return _orientationProperties;
      };
      _state = 'ready';
      mraid.dispatchEvent(new CustomEvent('ready'));
      global.mraid = mraid;
    }(window);
  `;
